import { Box, Grid, Stack } from '@mui/joy';
import { ICellRendererParams } from 'ag-grid-community';
import { t } from 'i18next';

import { StandardCost } from 'bom/elements';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { Margin, Price, WarningValidationIcon } from 'core/elements';
import { getAgGridTableCellData } from 'core/utils';
import { PricingHelper } from 'pricing/helpers';
import { QuotePartColId } from 'quotes/components/QuotePartList';
import { PriceBasedOn, QuoteEntityType } from 'quotes/enums';
import { QuotePartHelper, QuotePartPricingHelper } from 'quotes/helpers';
import { IQuotePart } from 'quotes/types';
import { getEffectiveSpaInfo } from 'quotes/utils';
import { Color } from 'theme/enums';

import { CostExceedsTooltip } from './CostExceedsTooltip';
import { CostInfoTooltip } from './CostInfoTooltip';
import { CostLockTooltip } from './CostLockTooltip';
import { ExternalPricingSourceTooltip } from './ExternalPricingSourceTooltip';
import { PrioritizeLegacyCostTooltip } from './PrioritizeLegacyCostTooltip';

interface IProps extends ICellRendererParams {
  quoteEntityType: QuoteEntityType;
  costDecimals?: number;
}

export const CostCell = ({
  quoteEntityType = QuoteEntityType.QUOTE,
  costDecimals = PricingHelper.DEFAULT_COST_DECIMALS,
  ...props
}: IProps) => {
  const data = getAgGridTableCellData(props, QuotePartColId.COST);
  if (!data) {
    return props?.value;
  }

  const {
    alternateVersionEnabled,
    computedCost,
    computedPurchaseCost,
    costDescription,
    costNote,
    computedCostSourceType,
    price,
    priceBasedOn,
  } = data as IQuotePart;

  const hasWarnings = QuotePartHelper.costHasWarnings(data);
  const warningType = QuotePartHelper.costWarningType(data);

  const isAlternateVersionEnabled = Boolean(alternateVersionEnabled);

  const purchaseCostExceedsStandard = QuotePartPricingHelper.doesPurchaseCostExceedStandard(
    computedPurchaseCost,
    computedCost,
    computedCostSourceType
  );

  if (!computedCost && !computedPurchaseCost) {
    return <Box sx={{ textAlign: 'right' }}>{DEFAULT_PLACEHOLDER}</Box>;
  }

  const effectiveSpa = getEffectiveSpaInfo(data);

  return (
    <Grid container alignItems="center" spacing={1}>
      <Grid xs={3}>
        <Stack spacing={1}>
          <CostLockTooltip quotePart={data} />
          {hasWarnings && <WarningValidationIcon type={warningType} />}
          <CostInfoTooltip
            quotePart={data}
            quoteEntityType={quoteEntityType}
            pricingData={effectiveSpa.pricingData}
            spaCostCurrency={effectiveSpa.spaCostCurrency}
          />
          {purchaseCostExceedsStandard && <CostExceedsTooltip />}
          <PrioritizeLegacyCostTooltip quotePart={data} />
        </Stack>
      </Grid>

      <Grid xs={9} sx={{ alignItems: 'flex-end', justifyContent: 'flex-end' }}>
        <ExternalPricingSourceTooltip
          pricingData={effectiveSpa.pricingData}
          spaCostCurrency={effectiveSpa.spaCostCurrency}
          isAlternateVersionEnabled={isAlternateVersionEnabled}
        >
          <Stack>
            <StandardCost
              cost={computedCost}
              decimals={costDecimals}
              fixedDecimals
              color={hasWarnings ? Color.DANGER : null}
              warningType={warningType}
              label={`${t('labelStandardCost')}:`}
            />

            <Price
              amount={computedPurchaseCost}
              decimals={costDecimals}
              fixedDecimals
              label={`${t('labelCost')}:`}
              notes={[costDescription, costNote]}
            />

            {priceBasedOn === PriceBasedOn.REPLACEMENT && (
              <Margin price={price} computedCost={computedCost} label={t('labelTrueMargin')} />
            )}
          </Stack>
        </ExternalPricingSourceTooltip>
      </Grid>
    </Grid>
  );
};
